/**
 * API 服务器命令
 * 启动和管理 Workflow CLI API 服务器
 */

import { Command } from 'commander';
import chalk from 'chalk';
import ora from 'ora';
import { getApiServer } from '../api/server.js';
import { logger } from '../utils/logger.js';
import { apiConfig } from '../api/config.js';

export function createApiCommand(): Command {
  const command = new Command('api');
  
  command
    .description('Manage Workflow API server')
    .option('-p, --port <port>', 'API server port', apiConfig.port.toString())
    .option('--no-sse', 'Disable SSE support')
    .action(async (options) => {
      console.log(chalk.blue('ℹ️  Use "workflow api start" to start the server'));
    });
  
  // 启动 API 服务器
  command
    .command('start')
    .description('Start the API server')
    .option('-p, --port <port>', 'API server port', apiConfig.port.toString())
    .option('--no-sse', 'Disable SSE support')
    .option('--no-graphql', 'Disable GraphQL support')
    .option('-d, --detach', 'Run in background')
    .action(async (options) => {
      const spinner = ora('Starting API server...').start();
      
      try {
        const port = parseInt(options.port) || apiConfig.port;
        const server = getApiServer({ 
          port,
          enableSSE: options.sse !== false,
          enableGraphQL: options.graphql !== false,
        });
        
        if (server.isServerRunning()) {
          spinner.fail('API server is already running');
          return;
        }
        
        await server.start();
        
        spinner.succeed(chalk.green(`✅ API server started on port ${port}`));
        console.log(chalk.cyan(`\n📍 Base URL: http://localhost:${port}${apiConfig.basePath}/${apiConfig.version}`));
        console.log(chalk.cyan(`📊 Health check: http://localhost:${port}/health`));
        
        if (options.sse !== false) {
          console.log(chalk.cyan(`📡 SSE endpoint: http://localhost:${port}${apiConfig.basePath}/${apiConfig.version}/sse/connect`));
        }
        
        if (options.graphql !== false) {
          console.log(chalk.cyan(`🎯 GraphQL endpoint: http://localhost:${port}/graphql`));
          console.log(chalk.cyan(`🔌 GraphQL subscriptions: ws://localhost:${port}/graphql`));
        }
        
        if (!options.detach) {
          console.log(chalk.yellow('\n⚠️  Server is running in foreground. Press Ctrl+C to stop.'));
          
          // 处理退出信号
          process.on('SIGINT', async () => {
            console.log(chalk.yellow('\n\nShutting down API server...'));
            await server.stop();
            process.exit(0);
          });
          
          process.on('SIGTERM', async () => {
            await server.stop();
            process.exit(0);
          });
        }
      } catch (error: any) {
        spinner.fail(chalk.red(`Failed to start API server: ${error.message}`));
        process.exit(1);
      }
    });
  
  // 停止 API 服务器
  command
    .command('stop')
    .description('Stop the API server')
    .action(async () => {
      const spinner = ora('Stopping API server...').start();
      
      try {
        const server = getApiServer();
        
        if (!server.isServerRunning()) {
          spinner.fail('API server is not running');
          return;
        }
        
        await server.stop();
        spinner.succeed(chalk.green('✅ API server stopped'));
      } catch (error: any) {
        spinner.fail(chalk.red(`Failed to stop API server: ${error.message}`));
        process.exit(1);
      }
    });
  
  // 查看 API 服务器状态
  command
    .command('status')
    .description('Check API server status')
    .action(async () => {
      try {
        const server = getApiServer();
        const isRunning = server.isServerRunning();
        
        if (isRunning) {
          const port = server.getPort();
          console.log(chalk.green(`✅ API server is running on port ${port}`));
          console.log(chalk.cyan(`📍 Base URL: http://localhost:${port}${apiConfig.basePath}/${apiConfig.version}`));
          
          // 尝试健康检查
          try {
            const response = await fetch(`http://localhost:${port}/health`);
            const health = await response.json();
            console.log(chalk.cyan(`📊 Health: ${health.status}`));
            console.log(chalk.cyan(`🔄 Version: ${health.version}`));
          } catch (error) {
            console.log(chalk.yellow('⚠️  Could not perform health check'));
          }
        } else {
          console.log(chalk.yellow('⚠️  API server is not running'));
        }
      } catch (error: any) {
        console.log(chalk.red(`Error checking status: ${error.message}`));
        process.exit(1);
      }
    });
  
  // 查看 API 路由
  command
    .command('routes')
    .description('List available API routes')
    .action(() => {
      console.log(chalk.blue('\n📋 Available API Routes:\n'));
      
      const routes = [
        { method: 'GET', path: '/health', description: 'Health check' },
        { method: 'GET', path: '/api/versions', description: 'API version information' },
        { method: 'GET', path: '/api/v2/tasks', description: 'List all tasks' },
        { method: 'GET', path: '/api/v2/tasks/:id', description: 'Get a specific task' },
        { method: 'GET', path: '/api/v2/tasks/:id/extended', description: 'Get extended task details' },
        { method: 'POST', path: '/api/v2/tasks', description: 'Create a new task' },
        { method: 'PUT', path: '/api/v2/tasks/:id', description: 'Update a task' },
        { method: 'DELETE', path: '/api/v2/tasks/:id', description: 'Delete a task' },
        { method: 'POST', path: '/api/v2/tasks/batch', description: 'Batch task operations' },
        { method: 'GET', path: '/api/v2/tasks/by-prd/:prdId', description: 'Get tasks by PRD ID' },
        { method: 'GET', path: '/api/v2/tasks/by-design/:designId', description: 'Get tasks by design ID' },
        { method: 'GET', path: '/api/v2/tasks/by-workflow/:workflowId', description: 'Get tasks by workflow ID' },
        { method: 'GET', path: '/api/v2/tasks/by-agent/:agentName', description: 'Get tasks by agent assignment' },
        { method: 'GET', path: '/api/v2/tasks/by-tech/:tech', description: 'Get tasks by tech stack' },
        { method: 'GET', path: '/api/v2/workflows', description: 'List all workflows' },
        { method: 'GET', path: '/api/v2/workflows/:id', description: 'Get a specific workflow' },
        { method: 'POST', path: '/api/v2/workflows', description: 'Create a new workflow' },
        { method: 'POST', path: '/api/v2/workflows/:id/start', description: 'Start a workflow' },
        { method: 'POST', path: '/api/v2/workflows/:id/stop', description: 'Stop a workflow' },
        { method: 'GET', path: '/api/v2/sse/connect', description: 'SSE connection endpoint' },
        { method: 'GET', path: '/api/v2/sse/clients', description: 'List SSE clients' },
        { method: 'POST', path: '/api/v2/sse/broadcast', description: 'Broadcast SSE event' },
        { method: 'GET', path: '/graphql', description: 'GraphQL playground (if enabled)' },
        { method: 'POST', path: '/graphql', description: 'GraphQL queries and mutations' },
        { method: 'WS', path: '/graphql', description: 'GraphQL subscriptions (WebSocket)' },
      ];
      
      const maxMethodLength = Math.max(...routes.map(r => r.method.length));
      const maxPathLength = Math.max(...routes.map(r => r.path.length));
      
      routes.forEach(route => {
        const method = route.method.padEnd(maxMethodLength);
        const path = route.path.padEnd(maxPathLength);
        const methodColor = {
          GET: chalk.green,
          POST: chalk.blue,
          PUT: chalk.yellow,
          DELETE: chalk.red,
          PATCH: chalk.magenta,
          WS: chalk.cyan,
        }[route.method] || chalk.white;
        
        console.log(`  ${methodColor(method)}  ${chalk.gray(path)}  ${route.description}`);
      });
      
      console.log('\n');
    });
  
  return command;
}